import { filterDOMProps } from "@react-aria/utils";
import type { DOMProps } from "@react-types/shared";
import { type CSSProperties, type FC, Fragment, type ReactNode, useMemo, useRef } from "react";
import { AnimatedTooltip } from "widgets";
import { useResizeObserver, useTooltip } from "widgets/hooks";

import { isColumnExpanded } from "@/redux/modules/table-data.ts";
import { nonBreakingSpace } from "@/settings/settings.js";

import { type ICustomColDef } from "../../DataTable.js";
import { type IGridContextProps } from "../../InMemoryTableWithSelectionModel.js";
import style from "../../styles/Cells.module.scss";

export interface ListCellItem {
  value: ReactNode;
  tooltip?: string;
  style?: CSSProperties; // needed?
  context?: IGridContextProps;
}

export interface ListCellContentProps {
  items: ListCellItem[]; // might be string or React elements
  colDef: ICustomColDef;
  style?: CSSProperties;
  context?: IGridContextProps;
}

const Content: FC<ListCellItem & { index: number; isExpanded?: boolean }> = (props) => {
  const triggerRef = useRef(null);
  const rect = useResizeObserver(triggerRef);
  // disable tooltip in error mode, otherwise validation errors are not shown properly in edit mode
  const { triggerProps, tooltipProps, state } = useTooltip(triggerRef, {
    isDisabled: props.context?.errorMode || !props.isExpanded || !rect?.widthOverflow,
  });
  const linkProps = filterDOMProps(props as DOMProps);

  return (
    <>
      <div
        key={props.index}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: props.value == nonBreakingSpace ? "pre-wrap" : "unset", // make sure the non-breaking space counts
          lineHeight: "19px",
          height: "19px",
          ...props.style,
        }}
        ref={triggerRef}
        {...triggerProps}
      >
        <span {...linkProps}>{props.value}</span>
      </div>
      <AnimatedTooltip domProps={tooltipProps} targetRef={triggerRef} state={state}>
        {props.tooltip}
      </AnimatedTooltip>
    </>
  );
};

/**
 * For complex table content, e.g. to display a list of links
 */
const ListCellContent: FC<ListCellContentProps> = (props) => {
  const triggerRef = useRef(null);
  const rect = useResizeObserver(triggerRef);

  const cellExpanded = useMemo<boolean>(() => {
    return props.context?.expansionPath ? isColumnExpanded(props.colDef, props.context?.expansionPath) : false;
  }, [props.colDef, props.context?.expansionPath]);

  const { triggerProps, tooltipProps, state } = useTooltip(triggerRef, {
    isDisabled: props.context?.errorMode || cellExpanded || !rect?.widthOverflow,
  });

  if (!props.items || !props.colDef) {
    return null;
  }

  if (cellExpanded) {
    return (
      <div className={style.expanded_values}>
        {props.items.map((item: ListCellItem, index: number) => (
          <Content
            {...item}
            style={props.style}
            index={index}
            isExpanded={cellExpanded}
            key={`list-content-item-${item}-${index}`}
          />
        ))}
      </div>
    );
  } else {
    const cellTooltip: string =
      props.items.length > 20
        ? `${props.items.length} values`
        : props.items
            .map((i) => i.tooltip)
            .filter((v) => v != undefined && v != nonBreakingSpace) // instead of !item os that the 0 value still is valid
            .join(", ");

    let listOfItems = props.items.filter(
      (item: ListCellItem) => item.value != undefined && item.value !== nonBreakingSpace,
    ); // instead of !item so that the 0 value still is valid

    // some logic to truncate the big lists that are overwhelming to render (although mostly hidden when the column is not expanded)
    const totalNumberOfInlineItems = listOfItems.length;
    let moreItems = null;
    if (totalNumberOfInlineItems > 20) {
      listOfItems = listOfItems.slice(0, 10);
      moreItems = <span style={{ fontWeight: "bold" }}>{` and ${totalNumberOfInlineItems - 10} other values`}</span>;
    }

    return (
      <>
        <div
          style={{ overflow: "hidden", textOverflow: "ellipsis", lineHeight: "19px", height: "19px", ...props.style }}
          ref={triggerRef}
          // do not spread triggerProps completely because this would ruin the cell marking by click
          onPointerEnter={triggerProps.onPointerEnter}
          onPointerLeave={triggerProps.onPointerLeave}
        >
          {listOfItems.map(
            (
              item: ListCellItem,
              index: number, // a simple join would not work for array of react elements
            ) => (
              <Fragment key={`item-${index}`}>
                {index > 0 ? <span style={{ display: "inline-block", verticalAlign: "middle" }}>,&nbsp;</span> : null}
                {item.value}
              </Fragment>
            ),
          )}
          {moreItems}
        </div>
        {cellTooltip && (
          <AnimatedTooltip targetRef={triggerRef} state={state} domProps={tooltipProps}>
            {cellTooltip}
          </AnimatedTooltip>
        )}
      </>
    );
  }
};

export default ListCellContent;
